// Type-only: ClientSetup imports this module, so no runtime cycle.
import type { ClientSetupProjectOrganisation, ProjectOrganisationApp } from "@/data/models/ClientSetup";
import type { MiraMode } from "@/data/models/MiraMode";

/**
 * The contract types a client can hold, as the IRES Admin Android app writes them to
 * `clients/{id}.contractTypes`.
 */
export enum ContractType {
  TESTING = "TESTING",
  PERMANENT_RENT = "PERMANENT_RENT",
  PURCHASE = "PURCHASE",
}

/**
 * Why a client is "Kein Echtbetrieb" (ticket 2610043): a test, demo or warehouse (Lager) account. Not
 * the contract type TESTING (Testphase), which is a real customer on a trial contract.
 */
export type NotLiveReason = "TEST" | "DEMO" | "WAREHOUSE";

/** The Stammdaten the IRES Admin Client edits (backlog #38), in the dialog's order: IRES Admin's identity fields. */
export const MASTER_DATA_FIELDS = [
  "clientName",
  "clientNumber",
  "street",
  "houseNumber",
  "zip",
  "city",
  "clientType",
  "iresProjectNumberPrefix",
] as const;

export type MasterDataField = typeof MASTER_DATA_FIELDS[number];

/** The Stammdaten exactly as stored (not trimmed), as `getClientForAdministration` serves them; null when absent. */
export type ClientMasterData = Record<MasterDataField, string | null>;

/** One field's change as `updateClientAdministration` takes it: the value the dialog loaded, and the new one. */
export type ClientMasterDataChanges = Partial<Record<MasterDataField, { expected: string | null; value: string | null }>>;

/**
 * A client's Verträge as `getClientForAdministration` serves them (backlog #38, slice 2): the types
 * sorted, start dates in epoch ms or null.
 */
export interface ClientContractData {
  contractTypes: ContractType[];
  hasServiceContractForFirmwaresAndConfigs: boolean;
  hasServiceContractForFirmwaresAndConfigsSince: number | null;
  hasWarrantyExtensionForPanels: boolean;
  hasWarrantyExtensionForPanelsSince: number | null;
}

export type ClientContractField = keyof ClientContractData;

/** One Verträge field's change as `updateClientAdministration` takes it (group CONTRACTS). */
export type ClientContractChanges = Partial<{
  [F in ClientContractField]: { expected: ClientContractData[F]; value: ClientContractData[F] };
}>;

/**
 * One Verlauf entry: a change the IRES Admin Client made to a client, as
 * `getClientAdministrationChangesForAdministration` serves it (no user id; dates in epoch ms).
 */
export interface ClientAdministrationChange {
  id: string;
  atMillis: number | null;
  byName: string | null;
  /** MASTER_DATA, CONTRACTS, LIVE_OPERATION, INFO_CONFIG_TARGET, BLOCKED, ACTIVE or DELETED; anything else is shown as stored. */
  group: string | null;
  changes: Array<{ field: string; before: unknown; after: unknown }>;
}

/**
 * The Verlauf's filter (#2610071): groups left out unless ticked in the card's filter. For now only the
 * "Als Mandant angemeldet" entries, which are not changes and would otherwise bury them.
 */
export type ChangeFilterGroup = "CLIENT_LOGIN";

/** Every group the Verlauf's filter offers, in its order. */
export const CHANGE_FILTER_GROUPS: ChangeFilterGroup[] = ["CLIENT_LOGIN"];

/** One page of the Verlauf, newest first; `nextCursor` is null at the end. */
export interface ClientAdministrationChangePage {
  changes: ClientAdministrationChange[];
  nextCursor: string | null;
}

/** The reasons in the order the dialog offers them. */
export const NOT_LIVE_REASONS: NotLiveReason[] = ["TEST", "DEMO", "WAREHOUSE"];

/** Why a client was deleted, the Löschen dialog's choice (#2610067); OTHER needs a note. */
export type DeleteReason = "CUSTOMER_LEFT" | "DUPLICATE" | "TEST_ACCOUNT" | "OTHER";

/** The reasons in the order the dialog offers them. */
export const DELETE_REASONS: DeleteReason[] = ["CUSTOMER_LEFT", "DUPLICATE", "TEST_ACCOUNT", "OTHER"];

/**
 * Why "Als Mandant anmelden" cannot open a client, as IRES Cloud names it. `SHARED_LOGIN`: the caller is
 * the shared IRES admin login, which names no person for the client's Verlauf (#2610071).
 */
export type ClientLoginRefusal = "SHARED_LOGIN" | "NO_CLIENT" | "PORTAL_CLIENT" | "ADMIN_APP_CLIENT" | "NO_IRES_ADMIN_USER";

/** Every refusal, for reading IRES Cloud's `details.reason`. */
export const CLIENT_LOGIN_REFUSALS: ClientLoginRefusal[] = [
  "SHARED_LOGIN",
  "NO_CLIENT",
  "PORTAL_CLIENT",
  "ADMIN_APP_CLIENT",
  "NO_IRES_ADMIN_USER",
];

/**
 * What `recalculateClientDryingsForAdministration` did (#2610077): a job was started, one was already
 * running, or there was nothing to recalculate.
 */
export type RecalculateDryingsStatus = "started" | "already_running" | "no_op";

/** `recalculateClientDryingsForAdministration`'s response: the start status and the job id (null for none). */
export interface RecalculateClientDryingsResponse {
  status: RecalculateDryingsStatus;
  jobId: string | null;
}

/** One change on the Status card, as `setClientStatusForAdministration` takes it. */
export type ClientStatusAction = "BLOCK" | "UNBLOCK" | "DEACTIVATE" | "ACTIVATE" | "DELETE" | "RESTORE";

/** Gesperrt, Aktiv, Gelöscht, each a plain boolean as IRES Cloud reads them (absent = not blocked, active, not deleted). */
export interface ClientStatusFlags {
  blocked: boolean;
  isActive: boolean;
  deleted: boolean;
}

/** Who changed a flag last and when, with the delete reason and the note where given. */
export interface ClientStatusChange {
  changedAtMillis: number | null;
  changedByName: string | null;
  reason: DeleteReason | null;
  note: string | null;
}

/** The Status card's flags and their last changes, as IRES Cloud serves them (#2610067). */
export interface ClientStatus {
  status: ClientStatusFlags;
  statusChanges: Record<keyof ClientStatusFlags, ClientStatusChange | null>;
}

/**
 * A client's "Kein Echtbetrieb" marker and its last change, as `getClientForAdministration` serves
 * it. Mira leaves every client with `live === false` out (IRES Cloud `docs/analytics/mira-loader.md`).
 */
export interface ClientLiveOperation {
  live: boolean;
  /** Set when `live` is false, null otherwise. */
  reason: NotLiveReason | null;
  changedAtMillis: number | null;
  /** Null for the accounts marked from Mira's review of 2026-09-30 by a script. */
  changedByName: string | null;
}

/**
 * "Mira: Alle Mandanten" on an IRES account (#2610111): its Mira shows every published Mandant added up.
 * Only IRES's own accounts may get it; IRES Cloud refuses it on any other.
 */
export interface ClientMiraAllClients {
  enabled: boolean;
  changedAtMillis: number | null;
  changedByName: string | null;
}

/**
 * One row of the admin portal's clients list, exactly as the `listClientsForAdministration`
 * callable returns it. Cloud normalises every field, so nothing here is a raw client document, and
 * no field the list does not show is ever sent (see IRES Cloud
 * `docs/apis/list-clients-for-administration.md`).
 */
export interface ClientAdministrationRow {
  id: string;
  clientName: string | null;
  email: string | null;
  clientNumber: string | null;
  /**
   * The four-character Mandantenkürzel, trimmed. Absent from IRES Cloud builds before 2026-10-12, so
   * optional.
   */
  iresProjectNumberPrefix?: string | null;
  /** IRES Office's "Ist ein MASTER-Mandant?"; absent from IRES Cloud builds before 2026-10-12. */
  isMasterClient?: boolean;
  /** The MASTER-Mandant the client belongs to (#2610074); null for none, absent from an older IRES Cloud. */
  masterClientId?: string | null;
  /** Why the client is "Kein Echtbetrieb"; null (or absent, from an older IRES Cloud) for live operation. */
  notLiveReason?: NotLiveReason | null;
  /** `IRES`, `CLIENT`, or another stored value (the Android app knows `OTHER`); null if absent. */
  clientType: string | null;
  contractTypes: ContractType[];
  hasServiceContractForFirmwaresAndConfigs: boolean;
  /** Epoch milliseconds, or null when not recorded. */
  hasServiceContractForFirmwaresAndConfigsSince: number | null;
  hasApp: boolean;
  targetAppApk: string | null;
  /** Tri-state: absent (null) counts as active, as it does in the Android app. */
  isActive: boolean | null;
  deleted: boolean;
  /** Gesperrt; absent from IRES Cloud builds before #2610067. */
  blocked?: boolean;
  /** Absent from IRES Cloud builds before ticket 2610024; read through `clientFigure`. */
  figures?: ClientFigures;
}

/**
 * A client's figures, recounted hourly by IRES Cloud (ticket 2610024; Cloud
 * `docs/projects/project-counters.md`). Null where the client has not been counted yet.
 */
export interface ClientFigures {
  /** Running and finished projects, deleted ones excluded. */
  numProjects: number | null;
  /** Archived projects, deleted ones excluded (Alte Projekte). */
  numOldProjects: number | null;
  /** Deleted projects, including those still waiting for the weekly archive run (Gelöscht). */
  numDeletedProjects: number | null;
  /** The whole archive, deleted projects included. */
  numArchivedProjects: number | null;
  numReports: number | null;
  /** The client detail's Gesamt row, by device kind. */
  numCurrentPanels: number | null;
  numCurrentComBoxes: number | null;
  numCurrentMinis: number | null;
  /**
   * Users not deleted, the detail's Benutzer (2026-10-12). Optional: IRES Cloud builds before then do
   * not send it.
   */
  numUsers?: number | null;
}

/** The clients list as the `listClientsForAdministration` callable returns it. */
export interface ClientAdministrationList {
  clients: ClientAdministrationRow[];
  /** When the figures were last counted, epoch ms; null before the first run or from an older Cloud. */
  figuresUpdatedAt: number | null;
}

/** The target versions on the detail overview, in the order the Android detail screen shows them. */
export const CLIENT_TARGET_VERSION_FIELDS = [
  "targetFirmwareName",
  "targetIotFirmwareName",
  "targetConfigName",
  "targetAppApk",
  "targetLeakDetectionInfoConfigVersion",
  "targetRoomInfoConfigVersion",
  "targetHtmlTipsAndTricksFile",
  "targetHtmlHistoryFile",
] as const;

export type ClientTargetVersionField = typeof CLIENT_TARGET_VERSION_FIELDS[number];

/**
 * One client's detail overview, exactly as the `getClientForAdministration` callable returns it.
 * Computed on the server: licences arrive only as `hasApp`, users only as a count.
 */
export interface ClientAdministrationDetail {
  id: string;
  clientName: string | null;
  email: string | null;
  clientNumber: string | null;
  companyName: string | null;
  zip: string | null;
  /** Street and house number, joined. */
  street: string | null;
  clientType: string | null;
  /** Shown as "Mandantenkürzel", as in the Android app. */
  iresProjectNumberPrefix: string | null;
  contractTypes: ContractType[];
  /** Tri-state: null is shown as "–", as the Android screen does. */
  hasWarrantyExtensionForPanels: boolean | null;
  hasServiceContractForFirmwaresAndConfigs: boolean | null;
  blocked: boolean | null;
  /** Licences or `hasAppSince`: the Android DETAIL screen's definition, not the list's. */
  hasApp: boolean;
  /** Each the client's own value, or the fleet default where the client has none. */
  targetVersions: Record<ClientTargetVersionField, string | null>;
  numUsers: number;
  numProjects: number | null;
  /** The archive's count: archived **and** deleted projects, both of which live in the archive. */
  numArchivedProjects: number | null;
  numReports: number | null;
  /** The same figures as the client's list row; absent from IRES Cloud builds before ticket 2610024. */
  figures?: ClientFigures;
  /** When the figures were last counted, epoch ms; null before the first run or from an older Cloud. */
  figuresUpdatedAt: number | null;
  /** Counts only; the per-user breakdown never leaves the server. */
  licences: ClientLicenceSummary;
  /** Null for a client never marked; absent from IRES Cloud builds before 2026-10-13. */
  liveOperation?: ClientLiveOperation | null;
  /** "Mira: Alle Mandanten" (#2610111); null when never set, absent from an older IRES Cloud. */
  miraAllClients?: ClientMiraAllClients | null;
  /** Whether the client is IRES's own account, the only kind that may get "Mira: Alle Mandanten". */
  isIresAccount?: boolean;
  /** The Status card's flags and who changed them; absent from IRES Cloud builds before #2610067, which then offer no changes. */
  status?: ClientStatus;
  /** The editable Stammdaten as stored; absent from IRES Cloud builds before 2026-10-13, which then offer no editing. */
  masterData?: ClientMasterData;
  /** The Verträge for the edit dialog; absent from IRES Cloud builds before 2026-10-14, which then offer no editing. */
  contracts?: ClientContractData;
  /**
   * The prefix the client's IRES project numbers use, fixed at its first number; editing the Kürzel
   * never changes it. Null before the first number; absent from IRES Cloud builds before 2026-10-13.
   */
  projectNumberPrefix?: string | null;
  /** The Test card (#2610070); absent from IRES Cloud builds before it. */
  testSettings?: ClientTestSettings;
  /** The Gruppe card (#2610074); absent from an older IRES Cloud, which leaves the card out. */
  group?: ClientGroup;
  /** The Produkte card (#2610089 stage 1); absent from an older IRES Cloud, which leaves the card out. */
  products?: ClientProducts;
  /** The Projekttypen card (#2610081); absent from an older IRES Cloud, which leaves the card out. */
  projectTypes?: ClientProjectTypes;
  /** The Projektorganisation card (2026-10-29); absent from an older IRES Cloud, which leaves the card out. */
  projectOrganisation?: ClientProjectOrganisation;
  /** The Systemkonfiguration card (#2610141); absent from an older IRES Cloud, which leaves the card out. */
  systemConfiguration?: ClientSystemConfiguration;
}

/** A test user as the Test card shows them. */
export interface ClientTestUser {
  id: string;
  /** First and last name, else the nickname; null for a user no longer found. */
  name: string | null;
  /** False for a deleted user or one no longer found: they get no test version. */
  active: boolean;
}

/**
 * A client's test settings (#2610070): the ERP-Simulator and the Test-App targets IRES Project gives
 * the test users. Changed only through `setClientTestSettingsForAdministration`.
 */
export interface ClientTestSettings {
  isTestSystem: boolean;
  /** The test app file ("HyDry-Firebase-2024.apk"), null for none. */
  appVersion: string | null;
  /** The test Beschreibung version, null for none. */
  roomInfoConfigVersion: string | null;
  testUsers: ClientTestUser[];
}

/** One side of a test settings change, as `setClientTestSettingsForAdministration` takes it. */
export interface ClientTestSettingsValue {
  isTestSystem: boolean;
  appVersion: string | null;
  roomInfoConfigVersion: string | null;
  userIds: string[];
}

/**
 * A client's licences on the detail: licence model 3 (used and purchased per type) when the client
 * has a licence record, else model 2 (its limits only, as IRES Admin shows them).
 */
export type ClientLicenceSummary =
  | {
    model: "V3";
    counts: Array<{ type: "basic" | "damageAssessmentAndLeakDetection" | "dryingAndRepair"; used: number; assigned: number }>;
  }
  | {
    model: "V2";
    limits: Array<{ type: "BASIC_LICENCE" | "DRYING_PROJECT_LICENCE" | "LEAK_DETECTION_PROJECT_LICENCE"; assigned: number }>;
  };

/** The assignment types the device counts show, in the Android detail screen's order. */
export const DEVICE_COUNT_ASSIGNMENT_TYPES = [
  "SOLD",
  "PERMANENT_RENT",
  "DAILY_RENT",
  "CONSIGNMENT_STOCK",
  "TESTING",
  "OTHER",
] as const;

export type DeviceCountAssignmentType = typeof DEVICE_COUNT_ASSIGNMENT_TYPES[number];

/** Panel types: "0" = HyDry, "1" = Slim, "2" = Edge. */
export type PanelTypeKey = "0" | "1" | "2";

export interface PanelTypeCounts {
  total: number;
  byPanelType: Record<PanelTypeKey, number>;
}

/**
 * The device counts on the client detail, exactly as the `getClientDeviceCountsForAdministration`
 * callable returns them (the rules are documented there: IRES Cloud
 * `docs/apis/list-clients-for-administration.md`).
 */
export interface ClientDeviceCounts {
  panels: {
    byAssignmentType: Record<DeviceCountAssignmentType, PanelTypeCounts>;
    total: number;
  };
  comBoxes: {
    byAssignmentType: Record<DeviceCountAssignmentType, number>;
    total: number;
  };
  /** Per the type of each Mini's open assignment; the total is the sum of the six types. */
  minis: {
    byAssignmentType: Record<DeviceCountAssignmentType, number>;
    total: number;
  };
  formerPanels: PanelTypeCounts;
}

/** One assignment of a device in the admin portal's device list. */
export interface ClientDeviceAssignment {
  /** A stored assignment type, AVAILABLE included. */
  assignmentType: string;
  startMillis: number | null;
  /** Null for an open assignment ("offen"). */
  endMillis: number | null;
}

/**
 * One device in the admin portal's device list, exactly as the `getClientDevicesForAdministration`
 * callable returns it. `countedType` and `former` are the same classification the Geräte counts use,
 * so filtering on them reproduces every count.
 */
export interface ClientDeviceRow {
  serialNumber: string;
  kind: "panel" | "comBox" | "mini";
  /** Panels only: 0 HyDry Panel, 1 Slim, 2 Edge; -1 when absent. */
  panelType: number;
  firmware: string | null;
  buildVersion: number | null;
  configurationName: string | null;
  displayFirmware: string | null;
  lastStatus: { createdAtMillis: number; source: string | null; lastPingMillis: number | null } | null;
  countedType: DeviceCountAssignmentType | null;
  currentAssignment: ClientDeviceAssignment | null;
  former: boolean;
  assignments: ClientDeviceAssignment[];
}

/**
 * One project in the admin portal's project list, exactly as the `getClientProjectsForAdministration`
 * callable returns it: names, numbers, dates, state, step flags and device count, nothing personal.
 */
export interface ClientProjectRow {
  id: string;
  /** `leakDetection` for the legacy `_ld` collections, `flex` for a `_flex` id, else `drying`. */
  kind: "drying" | "flex" | "leakDetection";
  projectName: string | null;
  /** The office (the client's own) project number. */
  projectNumber: string | null;
  projectNumberIres: string | null;
  /** The ERP partner's collective number (Sammelnummer). */
  projectNameErp: string | null;
  startMillis: number | null;
  endMillis: number | null;
  /** From the collection it was read from: running or finished; an archived project counts as finished. */
  finished: boolean;
  /** Read from the archive (IRES Office's Alte Projekte). */
  archived: boolean;
  /** `deleted` set or conclusion state DELETED, IRES Office's `isProjectDeleted`; only archived ones are listed. */
  deleted: boolean;
  /** `conclusion.conclusionState` as stored (ARCHIVED, MOVED_TO_FINISHED, ...), or null. */
  conclusionState: string | null;
  /** One entry per step box: true finished, false started, null not reached (8 boxes, 4 for `_ld`). */
  steps: Array<boolean | null>;
  numDevices: number | null;
  /** Absent on older overview entries: null means "not known", never zero. */
  numDevicesTotal: number | null;
}

/**
 * One report in the admin portal's report list, exactly as the `getClientReportsForAdministration`
 * callable returns it: metadata only, no storage path, no PDF, no creator or sender.
 */
export interface ClientReportRow {
  id: string;
  projectId: string | null;
  projectName: string | null;
  projectNumber: string | null;
  reportDateMillis: number | null;
  /** When it was last sent; null when never. */
  lastTimeSendMillis: number | null;
  reportType: string | null;
  extendedReportType: string | null;
  usage: string | null;
  extendedReportUsage: string | null;
  interimReportPhase: string | null;
  phaseComplete: boolean | null;
  /** The display file name, else the file name: how IRES Office names an uploaded document. */
  fileName: string | null;
  fileSizeInBytes: number | null;
  outdated: boolean;
  /** The stored file's extension, lower case (`pdf`, `csv`, …), or null; the path never reaches the browser. */
  fileType: string | null;
}

/** One page of the report list: the rows, the cursor for the next page (null at the end), the total on the first page. */
export interface ClientReportPage {
  reports: ClientReportRow[];
  nextCursor: string | null;
  total: number | null;
}

/**
 * One user in the admin portal's user list, exactly as the `getClientUsersForAdministration` callable
 * returns it: the IRES Admin fields only, no contact data, address, staff number or password data.
 */
export interface ClientUserRow {
  id: string;
  nickname: string | null;
  firstName: string | null;
  lastName: string | null;
  role: string | null;
  validationLevel: string | null;
  blocked: boolean;
  deleted: boolean;
  /** The licence names (`Licence` values), newest licence model first, as IRES Office reads them. */
  licences: string[];
}

/** The user list's data: the users, and the role names the client runs on (role to label). */
export interface ClientUserList {
  users: ClientUserRow[];
  roleLabels: Record<string, string>;
}

/** An archived project row, with the page it came from: a project on two pages keeps the higher one. */
export interface ClientArchivedProjectRow extends ClientProjectRow {
  pageNumber: number;
}

/** One slice of the archive: the rows, the cursor for the next slice (null at the end), the page count on the first. */
export interface ClientArchivedProjectSlice {
  projects: ClientArchivedProjectRow[];
  nextCursor: string | null;
  pageCount: number | null;
}

/** What IRES Cloud does with one mapped template value when it fills the PDF. */
export type PdfTemplateValueStatus = "RENDERED" | "UNKNOWN_TO_CLOUD" | "NOT_IMPLEMENTED" | "CHECKBOX_ALWAYS_OFF";

/** One form field of a PDF template and what is filled into it, as `getClientPdfTemplatesForAdministration` returns it. */
export interface PdfTemplateField {
  fieldName: string;
  category: string | null;
  /** IRES Cloud's German name, or null. */
  categoryLabel: string | null;
  values: Array<{ name: string; label: string | null; status: PdfTemplateValueStatus }>;
  /** Text before (`PREFIX`) or after (`POSTFIX`) a value; `id` is kept when the mapping is saved. */
  transformations: Array<{ id: string | null; value: string; type: string; text: string }>;
}

/** One PDF template in the admin portal: name, last save and its field mapping; no storage path. */
export interface PdfTemplateRow {
  id: string;
  name: string | null;
  fileName: string | null;
  /** IRES Admin overwrites the date on every save, so this is the last save. */
  lastSavedMillis: number | null;
  fields: PdfTemplateField[];
  /** Values that print blank or always unticked. */
  problemValueCount: number;
  hasFile: boolean;
}

/** A form field's kind, as IRES Admin derives it; a parent of child fields is a text field. */
export type PdfTemplateFormFieldType = "TEXT" | "CHECK_BOX" | "SIGNATURE" | "DROPDOWN";

/** One top-level form field of a template's PDF, as IRES Admin lists them. */
export interface PdfTemplateFormField {
  /** The key a mapping is stored under. */
  name: string;
  type: PdfTemplateFormFieldType;
  /** Named child fields grouped under it; 0 for an ordinary field. */
  childFieldCount: number;
}

/** Why IRES Cloud could not list a template's form fields. */
export type PdfTemplateFormFieldsUnavailable = "NO_FILE" | "FILE_MISSING" | "ENCRYPTED" | "UNREADABLE";

/** One category of IRES Admin's value catalogue, with the values it offers in IRES Admin's order. */
export interface PdfTemplateCatalogueCategory {
  name: string;
  label: string;
  /** `status` is what IRES Cloud does with the value; it only matters for the HyDry certificate. */
  values: Array<{ name: string; label: string; status: PdfTemplateValueStatus }>;
}

/** One template opened in the mapping editor, as `getClientPdfTemplateFormForAdministration` returns it. */
export interface PdfTemplateForm {
  template: PdfTemplateRow;
  /** The HyDry certificate, the only template IRES Cloud fills, is the only one whose statuses matter. */
  isHyDryCertificateTemplate: boolean;
  /** Null when the PDF could not be read; `formFieldsUnavailable` says why. */
  formFields: PdfTemplateFormField[] | null;
  formFieldsUnavailable: PdfTemplateFormFieldsUnavailable | null;
  catalogue: PdfTemplateCatalogueCategory[];
}

/** One field's mapping as the editor holds it and `saveClientPdfTemplateMappingForAdministration` takes it. */
export interface PdfTemplateFieldMapping {
  fieldName: string;
  category: string;
  /** In print order; `COMMA` may repeat. */
  values: string[];
  /** At most one `PREFIX` and one `POSTFIX` per value; `id` null for a new one. */
  transformations: Array<{ id: string | null; fieldValue: string; type: "PREFIX" | "POSTFIX"; value: string }>;
}

/** A client's (or the shared) templates, and which one the HyDry certificate uses (null for shared). */
export interface PdfTemplateList {
  templates: PdfTemplateRow[];
  hyDryCertificateTemplateId: string | null;
}

/** One Mandant on the Gruppe card (#2610074), named as IRES Office names clients. */
export interface ClientGroupClient {
  id: string;
  name: string;
  isActive: boolean;
  deleted: boolean;
}

/** A client's group as IRES Cloud serves it (#2610074): a MASTER-Mandant with its members, or a member. */
export interface ClientGroup {
  isMaster: boolean;
  members: ClientGroupClient[];
  /** "Einstellungen des Mitgliedsmandanten benutzen": the member whose settings the master took over. */
  settingsSource: ClientGroupClient | null;
  /** For a member: its MASTER-Mandant. */
  master: ClientGroupClient | null;
}

/** A group as the Gruppe dialog edits it and `setClientGroupForAdministration` takes it. */
export interface ClientGroupValue {
  isMaster: boolean;
  memberClientIds: string[];
  settingsSourceClientId: string | null;
}

/** Why IRES Cloud refused a group change (`details.reason`). */
export type ClientGroupRefusal =
  | "INVALID"
  | "NO_CLIENT"
  | "DELETED_CLIENT"
  | "CHANGED_MEANWHILE"
  | "IS_MEMBER"
  | "HAS_MEMBERS"
  | "NOT_A_CANDIDATE"
  | "NOT_A_MEMBER"
  | "DUPLICATE_BRANCH_NUMBERS"
  | "SHARED_LOGIN";

/** Every refusal the dialog names. */
export const CLIENT_GROUP_REFUSALS: ClientGroupRefusal[] = [
  "INVALID",
  "NO_CLIENT",
  "DELETED_CLIENT",
  "CHANGED_MEANWHILE",
  "IS_MEMBER",
  "HAS_MEMBERS",
  "NOT_A_CANDIDATE",
  "NOT_A_MEMBER",
  "DUPLICATE_BRANCH_NUMBERS",
  "SHARED_LOGIN",
];

/**
 * The four products as one switch each (#2610089 stage 1): GZ / GL projects in IRES Office, IRES
 * Project, IRES Work, and a REST interface. IRES Office itself is always available; the switch only
 * adds GZ / GL projects in it. The switches are the new truth, and `projectManagement` is written
 * derived from them for its old readers (see `docs/improvements/client-setup-plan.md`).
 */
export interface ClientProductsValue {
  /** GZ / GL projects in IRES Office. */
  officeDeviceAssignment: boolean;
  /** IRES Project. */
  project: boolean;
  /** IRES Work. */
  work: boolean;
  /** A REST interface to an external system (Schnittstelle). */
  interface: boolean;
}

/**
 * A client's products as `getClientForAdministration` serves them (#2610089). `derived` is true before
 * the backfill, when the switches are computed from the old `projectManagement` choice and not stored
 * yet; the card then notes it.
 */
export interface ClientProducts extends ClientProductsValue {
  derived: boolean;
}

/** Why IRES Cloud refused a products change (`details.reason`), as `setClientProductsForAdministration` names them. */
export type ClientProductsRefusal =
  | "INVALID_CLIENT"
  | "INVALID"
  | "NO_CLIENT"
  | "DELETED_CLIENT"
  | "CHANGED_MEANWHILE"
  | "SHARED_LOGIN";

/** Every refusal the dialog reads, for matching IRES Cloud's `details.reason`. */
export const CLIENT_PRODUCTS_REFUSALS: ClientProductsRefusal[] = [
  "INVALID_CLIENT",
  "INVALID",
  "NO_CLIENT",
  "DELETED_CLIENT",
  "CHANGED_MEANWHILE",
  "SHARED_LOGIN",
];

/**
 * The project types IRES gives a client (#2610081), as `getClientForAdministration` serves them: the
 * types its products allow (`possible`), the ones it gets (`allowed`), and those switched on in the
 * client's own "Mögliche Projekttypen" table. Type ids as in `ALL_PROJECT_TYPES` (`@/data/models/ProjectDetails`).
 */
export interface ClientProjectTypes {
  possible: number[];
  allowed: number[];
  /** False when IRES never narrowed the client: it gets every type of its products. */
  restricted: boolean;
  /** Null when the client has no table stored (each app then shows its own default). */
  activeInClientTable: number[] | null;
  /** The MASTER-Mandant whose list and table a member uses, or null when it has its own. */
  followsMasterClientId: string | null;
}

/** Why IRES Cloud refused a project types change (`details.reason`), as `setClientProjectTypesForAdministration` names them. */
export type ClientProjectTypesRefusal =
  | "INVALID_CLIENT"
  | "INVALID"
  | "EMPTY"
  | "NO_CLIENT"
  | "DELETED_CLIENT"
  | "CHANGED_MEANWHILE"
  | "FOLLOWS_MASTER"
  | "SHARED_LOGIN";

/** Every refusal the dialog reads, for matching IRES Cloud's `details.reason`. */
export const CLIENT_PROJECT_TYPES_REFUSALS: ClientProjectTypesRefusal[] = [
  "INVALID_CLIENT",
  "INVALID",
  "EMPTY",
  "NO_CLIENT",
  "DELETED_CLIENT",
  "CHANGED_MEANWHILE",
  "FOLLOWS_MASTER",
  "SHARED_LOGIN",
];

/**
 * A client's Projektorganisation (the four "... ausführen?" answers), as `getClientForAdministration`
 * serves it since 2026-10-29: the answers the apps use, whether they are stored, the apps the products
 * offer, and the master a following member uses.
 */
export interface ClientProjectOrganisation {
  value: ClientSetupProjectOrganisation;
  /** False when nothing is stored: the apps use their defaults, which `value` shows. */
  stored: boolean;
  /** The apps the products offer, in IRES Office's order. */
  offered: ProjectOrganisationApp[];
  /** Whether the products include projects at all; without, there is nothing to organise. */
  hasProjects: boolean;
  /** The MASTER-Mandant whose answers a member uses, or null when it has its own. */
  followsMasterClientId: string | null;
}

/** Why IRES Cloud refused a Projektorganisation change (`details.reason`), as `setClientProjectOrganisationForAdministration` names them. */
export type ClientProjectOrganisationRefusal =
  | "INVALID_CLIENT"
  | "INVALID"
  | "NO_CLIENT"
  | "DELETED_CLIENT"
  | "CHANGED_MEANWHILE"
  | "FOLLOWS_MASTER"
  | "NO_PROJECTS"
  | "SHARED_LOGIN";

/** Every refusal the dialog reads, for matching IRES Cloud's `details.reason`. */
export const CLIENT_PROJECT_ORGANISATION_REFUSALS: ClientProjectOrganisationRefusal[] = [
  "INVALID_CLIENT",
  "INVALID",
  "NO_CLIENT",
  "DELETED_CLIENT",
  "CHANGED_MEANWHILE",
  "FOLLOWS_MASTER",
  "NO_PROJECTS",
  "SHARED_LOGIN",
];

/**
 * A client's Systemkonfiguration as the card shows and edits it (#2610141): every switch of IRES Office's
 * Einstellungen > Systemkonfiguration in its order, ASSIST's scope, and Mira.
 */
export interface ClientSystemConfigurationValue {
  enableGeoEncoding: boolean;
  enableTracking: boolean;
  enableVehicleManagement: boolean;
  enableScheduling: boolean;
  enableAllDevicesManagement: boolean;
  enableDeviceManagementTile: boolean;
  enableTimeTracking: boolean;
  enablePersonalChat: boolean;
  enableProjectChat: boolean;
  enableDeviceCheckManagement: boolean;
  enableAiPredictions: boolean;
  enableBillingCatalogs: boolean;
  renoActivationScope: "ALL_DRYINGS" | "ACTIVE_AND_NEW_ONLY";
  miraMode: MiraMode;
}

/**
 * A client's Systemkonfiguration, as `getClientForAdministration` serves it since #2610141: the values the
 * client uses, whether a document is stored, and the master a following member uses.
 */
export interface ClientSystemConfiguration {
  value: ClientSystemConfigurationValue;
  /** False when nothing is stored: IRES Office falls back to the client's older switches, which `value` shows. */
  stored: boolean;
  /** The MASTER-Mandant whose configuration a member uses, or null when it has its own. */
  followsMasterClientId: string | null;
}

/** Why IRES Cloud refused a Systemkonfiguration change (`details.reason`), as `setClientSystemConfigurationForAdministration` names them. */
export type ClientSystemConfigurationRefusal =
  | "INVALID_CLIENT"
  | "INVALID"
  | "NO_CLIENT"
  | "DELETED_CLIENT"
  | "CHANGED_MEANWHILE"
  | "FOLLOWS_MASTER"
  | "SHARED_LOGIN";

/** Every refusal the dialog reads, for matching IRES Cloud's `details.reason`. */
export const CLIENT_SYSTEM_CONFIGURATION_REFUSALS: ClientSystemConfigurationRefusal[] = [
  "INVALID_CLIENT",
  "INVALID",
  "NO_CLIENT",
  "DELETED_CLIENT",
  "CHANGED_MEANWHILE",
  "FOLLOWS_MASTER",
  "SHARED_LOGIN",
];

/** How ASSIST's activation went after a save that switched ASSIST on: its status, failed, or null for none. */
export type AssistActivationOutcome = { status: "started" | "already_running" | "no_op" } | { failed: true } | null;

